import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCategories } from '../context/CategoriesContext';
import SearchSuggestions from './SearchSuggestions';
import { usePWA } from '../context/PWAContext';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { user } = useAuth();
  const { cartItems, toggleCart } = useCart();
  const { wishlistItems } = useWishlist();
  const { categories: dbCategories, subcategories: dbSubcategories, subSubcategories: dbSubSubcategories } = useCategories();
  const { isInstallable, isInstalled, triggerInstall } = usePWA();
  const navigate = useNavigate();

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const wishlistCount = wishlistItems.length;

  const handleProfileClick = () => {
    if (user) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleDropdownToggle = (category) => {
    setOpenDropdown(openDropdown === category ? null : category);
  };

  const categoryNavItems = [
    { name: 'SHOP ALL', url: '/shop-all' },
    { name: 'BRANDS', url: '/brands' },
    ...(dbCategories?.map(c => ({ name: c.name.toUpperCase(), url: `/${c.slug}`, align: 'left' })) || []),
    { name: 'GIFT CARD', url: '/gift-card' }
  ];

  const categories = categoryNavItems;

  const dropdownCategoriesNav = dbCategories?.length > 0
    ? dbCategories.map(c => c.name.toUpperCase())
    : [];

  const dropdownCategories = dropdownCategoriesNav;

  const getSubcategoriesForCategory = (categorySlug) => {
    const category = dbCategories?.find(c => c.slug === categorySlug);
    if (!category) return [];

    return dbSubcategories
      ?.filter(s => s.category_id === category.id)
      .map(sub => {
        const subSubItems = dbSubSubcategories
          ?.filter(ss => ss.subcategory_id === sub.id)
          .map(ss => ({
            name: ss.name,
            url: `/${categorySlug}/${sub.slug}/${ss.slug}`
          }));

        return {
          name: sub.name.toUpperCase(),
          slug: sub.slug,
          items: subSubItems || [{ name: sub.name, url: `/${categorySlug}/${sub.slug}` }]
        };
      }) || [];
  };

  const categorySubcategoriesMap = {};
  if (dbCategories?.length > 0 && dbSubcategories?.length > 0) {
    dbCategories.forEach(cat => {
      categorySubcategoriesMap[cat.name.toUpperCase()] = getSubcategoriesForCategory(cat.slug);
    });
  }

  return (
    <header className="sticky top-0 z-[1000] bg-primary shadow-sm">
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <button className="lg:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>

            <div className="flex-shrink-0">
              <Link to="/">
                <img
                  src="/luxe-looks-logo2.jpg"
                  alt="Luxe Looks"
                  className="h-24 w-auto"
                />
              </Link>
            </div>

            <div className="flex-1 max-w-lg mx-8 lg:order-2 hidden lg:block">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Search products, brands..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="w-full px-4 py-2.5 bg-gray-50 border-0 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 rounded-full pr-28"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 -translate-y-1/2 bg-gold hover:bg-yellow-500 text-white text-sm font-medium px-5 py-2 rounded-full transition-colors"
                >
                  Search
                </button>
                <SearchSuggestions
                   query={searchQuery}
                   onSelect={handleSuggestionSelect}
                   isVisible={showSuggestions && searchQuery.length > 1}
                 />
              </form>
            </div>

            <div className="flex items-center gap-5 lg:order-3 order-2">
              {isInstallable && !isInstalled && (
                <button
                  onClick={triggerInstall}
                  title="Install Luxe Looks App"
                  className="flex flex-col items-center gap-0.5 text-white
                             hover:text-gold transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg"
                       className="w-5 h-5" fill="none"
                       viewBox="0 0 24 24" stroke="currentColor"
                       strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2
                             M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  <span className="text-[9px] font-medium leading-none">
                    Install
                  </span>
                </button>
              )}
              <Link to="/wishlist" className="text-gold hover:text-white transition-colors relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {wishlistCount > 0 && (
                  <span className="absolute -top-2 -right-2 h-4 w-4 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>
              <button onClick={toggleCart} className="text-gold hover:text-white transition-colors relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 8a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 h-4 w-4 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
              <button onClick={handleProfileClick} className="text-gold hover:text-white transition-colors relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {user && <span className="absolute -top-1 -right-1 w-2 h-2 bg-gold rounded-full"></span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden max-w-7xl mx-auto px-6 py-3 bg-white">
        <form onSubmit={handleSearch} className="relative">
          <input
            type="text"
            placeholder="Search products, brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="w-full px-4 py-2.5 bg-gray-50 border-0 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 rounded-full pr-28"
          />
          <button
            type="submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-gold hover:bg-yellow-500 text-white text-sm font-medium px-5 py-2 rounded-full transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      <nav className="hidden lg:block bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <ul className="flex items-center justify-between w-full py-3.5">
            {categories.map((cat) => (
              <li key={cat.name} className="relative group">
                <a
                  href={cat.url}
                  className="text-[11px] font-medium tracking-[0.15em] text-gray-900 hover:text-gold transition-colors inline-flex items-center gap-1 uppercase"
                >
                  {cat.name}
                  {dropdownCategories.includes(cat.name) && (
                    <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </a>
                {dropdownCategories.includes(cat.name) && categorySubcategoriesMap[cat.name]?.length > 0 && (
                  <div className="absolute top-full bg-white border-t border-gray-200 shadow-xl hidden group-hover:block left-0" style={{ minWidth: '200px', maxHeight: '60vh', overflowY: 'auto' }}>
                     <div className="flex gap-2 p-2" style={{ flexWrap: 'wrap' }}>
                      {categorySubcategoriesMap[cat.name].map((sub, idx) => (
                        <div key={sub.name} className="flex flex-col gap-2 min-w-[140px] mb-2" style={{ flex: '1 1 200px', maxWidth: '280px' }}>
                          <h4 className="text-[11px] font-semibold text-gray-900 uppercase tracking-[0.1em] mb-1">
                            <a href={`/${cat.url.split('/')[1]}/${sub.slug}`} className="hover:text-gold">{sub.name}</a>
                          </h4>
                          <ul className="space-y-1">
                            {sub.items.map((item) => (
                              <li key={item.name}>
                                <a href={item.url} className="text-sm text-gray-500 hover:text-gray-900 hover:translate-x-1 transition-all duration-200 flex items-center justify-between w-full group/item whitespace-nowrap">
                                  <span>{item.name}</span>
                                  <span className="opacity-0 group-hover/item:opacity-100 text-gray-400 text-xs transition-opacity">&rarr;</span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white max-h-[70vh] overflow-y-auto">
          <ul className="py-2 px-4 space-y-2">
            {categories.map((cat) => (
              <li key={cat.name}>
                <div>
                  <div
                    className="flex items-center justify-between text-sm text-gray-900 hover:text-gray-600 py-2 cursor-pointer"
                    onClick={() => {
                      if (dropdownCategories.includes(cat.name)) {
                        handleDropdownToggle(cat.name);
                      }
                    }}
                  >
                    {dropdownCategories.includes(cat.name) ? (
                      <span>{cat.name}</span>
                    ) : (
                      <a href={cat.url}>{cat.name}</a>
                    )}
                    {dropdownCategories.includes(cat.name) && (
                      <svg className={`w-4 h-4 transition-transform ${openDropdown === cat.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                  {openDropdown === cat.name && categorySubcategoriesMap[cat.name]?.length > 0 && (
                    <div className="pl-2 py-3 space-y-2">
                      {categorySubcategoriesMap[cat.name].map((sub) => (
                        <div key={sub.name}>
                          <Link to={`/${cat.url.split('/')[1]}/${sub.slug}`} onClick={() => setMobileMenuOpen(false)} className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1 pl-2 hover:text-gold transition-colors block">{sub.name}</Link>
                          <ul className="pl-4 space-y-1">
                            {sub.items.map((item) => (
                              <li key={item.name}><a href={item.url} className="block text-sm text-gray-600 py-1 pl-2 border-l border-gray-200 hover:text-gray-900 hover:border-gray-400">{item.name}</a></li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;